import type * as React from 'react';
import { getPaginated } from '@Domain/Api';
import type { BeneficiaryListItemPayload } from '@Application/Beneficiaries/Beneficiaries/Actions/beneficiariesFetched';
import beneficiaryDirectoryFetched, {
	type BeneficiaryDirectoryFetchedAction,
} from '@Application/Beneficiaries/BeneficiaryDirectory/Actions/beneficiaryDirectoryFetched';
import beneficiaryDirectoryFetchFailed, {
	type BeneficiaryDirectoryFetchFailedAction,
} from '@Application/Beneficiaries/BeneficiaryDirectory/Actions/beneficiaryDirectoryFetchFailed';
import type FetchBeneficiaryDirectory from '@Application/Beneficiaries/BeneficiaryDirectory/Queries/FetchBeneficiaryDirectory';
import extractMessages from '@Infra/FetchLib/ApiError/extractMessages';
import parameters from '../../../parameters';

type Actions = BeneficiaryDirectoryFetchedAction | BeneficiaryDirectoryFetchFailedAction;

// `getPaginated`'s generic constraint wants a mutable `results` array (it pushes into it while walking
// pages): declared locally, distinct from the frozen `BeneficiaryDirectoryFetchedPayload` the action
// carries, into which this widens without a cast.
type PaginatedBeneficiaryDirectory = {
	totalRecords: number;
	results: BeneficiaryListItemPayload[];
};

// No `status` filter is sent: the directory must name a beneficiary from a past run even once archived.
export default (query: FetchBeneficiaryDirectory) => {
	return async (dispatch: React.Dispatch<Actions>): Promise<Actions> => {
		try {
			const url = new URL(`${parameters.api.host}/api/accounts/${query.accountId}/beneficiaries`);
			url.searchParams.append('facilityIds', String(query.facilityId));

			if (query.assignedUserId !== null) {
				url.searchParams.set('assignedUserId', String(query.assignedUserId));
			}

			const payload = await getPaginated<PaginatedBeneficiaryDirectory>(url.toString(), {
				page: 0,
				rowsPerPage: 200,
			});

			const action = beneficiaryDirectoryFetched(payload, query.facilityId, query.assignedUserId);
			dispatch(action);
			return action;
		} catch (error) {
			const action = beneficiaryDirectoryFetchFailed(extractMessages(error));
			dispatch(action);
			return Promise.reject(action);
		}
	};
};
